import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./login.css";
import API from "../services/api";
import { setLoginTime } from "../utils/auth";

/* ─── tiny inline icons ─── */
const IconMail = () => (
  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6">
    <rect x="2" y="4" width="16" height="13" rx="2"/>
    <path d="M2 7l8 5 8-5"/>
  </svg>
);
const IconLock = () => (
  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6">
    <rect x="4" y="9" width="12" height="9" rx="2"/>
    <path d="M7 9V6a3 3 0 016 0v3"/>
  </svg>
);
const IconKey = () => (
  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6">
    <circle cx="8" cy="10" r="4"/>
    <path d="M12 10h6M16 8v4"/>
  </svg>
);
const IconEye = ({ off }) => off ? (
  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6">
    <path d="M3 3l14 14M8.5 8.6A3 3 0 0111.4 11.5M6.3 6.4C4.8 7.4 3.6 8.6 3 10c1.3 2.7 4 5 7 5a7 7 0 003.5-.95M10.9 5.1A7 7 0 0117 10c-.4.9-1 1.8-1.8 2.5"/>
  </svg>
) : (
  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6">
    <path d="M1.5 10S5 4 10 4s8.5 6 8.5 6-3.5 6-8.5 6S1.5 10 1.5 10z"/>
    <circle cx="10" cy="10" r="2.5"/>
  </svg>
);
const IconArrow = () => (
  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M4 10h12M11 6l4 4-4 4"/>
  </svg>
);
const IconCheck = () => (
  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.2">
    <path d="M4 10l5 5 7-8"/>
  </svg>
);

/* ─── step constants ─── */
const STEP_LOGIN = "login";
const STEP_EMAIL = "email";
const STEP_OTP   = "otp";
const STEP_RESET = "reset";
const STEP_DONE  = "done";

/* ─── validators ─── */
const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());

function Login() {
  const navigate = useNavigate();

  /* ── Login state ── */
  const [email,      setEmail]      = useState("");
  const [password,   setPassword]   = useState("");
  const [showPw,     setShowPw]     = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState("");

  /* ── Forgot password state ── */
  const [step,        setStep]        = useState(STEP_LOGIN);
  const [fpEmail,     setFpEmail]     = useState("");
  const [otp,         setOtp]         = useState(["","","","","",""]);
  const [newPw,       setNewPw]       = useState("");
  const [confirmPw,   setConfirmPw]   = useState("");
  const [showNewPw,   setShowNewPw]   = useState(false);
  const [showConfPw,  setShowConfPw]  = useState(false);
  const [fpLoading,   setFpLoading]   = useState(false);
  const [fpError,     setFpError]     = useState("");
  const [fpSuccess,   setFpSuccess]   = useState("");
  const [resendTimer, setResendTimer] = useState(0);

  /* ── field-level errors ── */
  const [errs, setErrs] = useState({});

  /* ── On mount: clear session but restore remembered email ── */
  useEffect(() => {
    // Clear session tokens but keep remember-me data
    localStorage.removeItem("token");
    localStorage.removeItem("user");

    const remembered = localStorage.getItem("remember_email");
    const rememberedPw = localStorage.getItem("remember_password");
    if (remembered) {
      setEmail(remembered);
      setRememberMe(true);
      if (rememberedPw) setPassword(atob(rememberedPw)); // base64 decode
    }
  }, []);

  /* ════════ LOGIN ════════ */
  const validateLogin = () => {
    const e = {};
    if (!email)                   e.email    = "Email is required";
    else if (!isValidEmail(email)) e.email   = "Enter a valid email";
    if (!password)                e.password = "Password is required";
    else if (password.length < 6) e.password = "Minimum 6 characters";
    setErrs(e);
    return !Object.keys(e).length;
  };

  const handleLogin = () => {
    setError("");
    if (!validateLogin()) return;
    setLoading(true);
    API.post("/login", { email, password })
      .then((res) => {
        if (res.data.status) {
          const userData = res.data.data;

          // Handle Remember Me
          if (rememberMe) {
            localStorage.setItem("remember_email",    email);
            localStorage.setItem("remember_password", btoa(password)); // base64 encode
          } else {
            localStorage.removeItem("remember_email");
            localStorage.removeItem("remember_password");
          }

          localStorage.setItem("token", userData.token);
          localStorage.setItem("user",  JSON.stringify(userData.user));
          setLoginTime();
          navigate("/dashboard");
        } else {
          setError(res.data.message || "Login failed");
        }
      })
      .catch((err) => {
        setError(err.response?.data?.message || "Server not reachable");
      })
      .finally(() => setLoading(false));
  };

  /* ════════ FORGOT — SEND OTP ════════ */
  const startResendTimer = () => {
    setResendTimer(60);
    const t = setInterval(() => {
      setResendTimer(prev => {
        if (prev <= 1) { clearInterval(t); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const validateFpEmail = () => {
    const e = {};
    if (!fpEmail)                   e.fpEmail = "Email is required";
    else if (!isValidEmail(fpEmail)) e.fpEmail = "Enter a valid email";
    setErrs(e);
    return !Object.keys(e).length;
  };

  const handleSendOtp = () => {
    setFpError(""); setFpSuccess("");
    if (!validateFpEmail()) return;
    setFpLoading(true);
    API.post("/forgot-password", { email: fpEmail.trim() })
      .then((res) => {
        if (res.data.success) {
          setFpSuccess("OTP sent! Check your email.");
          setStep(STEP_OTP);
          startResendTimer();
        } else {
          setFpError(res.data.message || "Failed to send OTP");
        }
      })
      .catch((err) => {
        setFpError(err.response?.data?.message || "Server not reachable");
      })
      .finally(() => setFpLoading(false));
  };

  /* ════════ FORGOT — VERIFY OTP ════════ */
  const handleOtpChange = (val, idx) => {
    if (!/^\d?$/.test(val)) return;
    const next = [...otp];
    next[idx] = val;
    setOtp(next);
    setErrs({});
    if (val && idx < 5) {
      document.getElementById(`otp-${idx + 1}`)?.focus();
    }
  };

  const handleOtpKeyDown = (e, idx) => {
    if (e.key === "Backspace" && !otp[idx] && idx > 0) {
      document.getElementById(`otp-${idx - 1}`)?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pasted.length === 6) {
      setOtp(pasted.split(""));
      document.getElementById("otp-5")?.focus();
    }
  };

  const handleVerifyOtp = () => {
    setFpError(""); setFpSuccess("");
    const otpStr = otp.join("");
    if (otpStr.length < 6) { setErrs({ otp: "Enter the 6-digit OTP" }); return; }
    setFpLoading(true);
    API.post("/verify-otp", { email: fpEmail.trim(), otp: otpStr })
      .then((res) => {
        if (res.data.success) { setStep(STEP_RESET); setFpSuccess(""); }
        else { setFpError(res.data.message || "Invalid OTP"); }
      })
      .catch((err) => { setFpError(err.response?.data?.message || "Server not reachable"); })
      .finally(() => setFpLoading(false));
  };

  /* ════════ FORGOT — RESET PASSWORD ════════ */
  const validateReset = () => {
    const e = {};
    if (!newPw)              e.newPw = "Password is required";
    else if (newPw.length < 6) e.newPw = "Minimum 6 characters";
    if (!confirmPw)          e.confirmPw = "Please confirm your password";
    else if (newPw !== confirmPw) e.confirmPw = "Passwords do not match";
    setErrs(e);
    return !Object.keys(e).length;
  };

  const handleResetPassword = () => {
    setFpError(""); setFpSuccess("");
    if (!validateReset()) return;
    setFpLoading(true);
    API.post("/reset-password", {
      email:                 fpEmail.trim(),
      password:              newPw,
      password_confirmation: confirmPw,
    })
      .then((res) => {
        if (res.data.success) { setStep(STEP_DONE); }
        else { setFpError(res.data.message || "Reset failed"); }
      })
      .catch((err) => { setFpError(err.response?.data?.message || "Server not reachable"); })
      .finally(() => setFpLoading(false));
  };

  /* ════════ HELPERS ════════ */
  const backToLogin = () => {
    setStep(STEP_LOGIN);
    setFpEmail(""); setOtp(["","","","","",""]); setNewPw(""); setConfirmPw("");
    setFpError(""); setFpSuccess(""); setErrs({});
  };

  /* ════════ RENDER RIGHT PANEL ════════ */
  const renderForm = () => {

    /* ── LOGIN ── */
    if (step === STEP_LOGIN) return (
      <>
        <div className="lf-brand">
          <div className="lf-badge">FL</div>
          <div>
            <div className="lf-brand-name">
              Hulm IT Solution Contracting
              <small>Field Job Tracking System</small>
            </div>
          </div>
        </div>

        <div className="lf-title">Welcome back</div>
        <div className="lf-sub">Sign in to manage field jobs, track activities and view reports.</div>

        {error && <div className="lf-alert lf-alert-error">{error}</div>}

        <label className="lf-label">Email Address</label>
        <div className={`lf-input-wrap ${errs.email ? "lf-input-err" : ""}`}>
          <IconMail />
          <input
            className="lf-input"
            type="email"
            placeholder="admin@example.com"
            value={email}
            onChange={(e) => { setEmail(e.target.value); setErrs(p => ({...p, email: ""})); }}
            onKeyDown={(e) => e.key === "Enter" && handleLogin()}
          />
        </div>
        {errs.email && <p className="lf-field-err">{errs.email}</p>}

        <label className="lf-label" style={{ marginTop: 12 }}>Password</label>
        <div className={`lf-input-wrap ${errs.password ? "lf-input-err" : ""}`}>
          <IconLock />
          <input
            className="lf-input"
            type={showPw ? "text" : "password"}
            placeholder="••••••••"
            value={password}
            onChange={(e) => { setPassword(e.target.value); setErrs(p => ({...p, password: ""})); }}
            onKeyDown={(e) => e.key === "Enter" && handleLogin()}
          />
          <button className="lf-eye" onClick={() => setShowPw(v => !v)} type="button">
            <IconEye off={showPw} />
          </button>
        </div>
        {errs.password && <p className="lf-field-err">{errs.password}</p>}

        {/* ── Remember Me + Forgot row ── */}
        <div className="lf-row">
          <label className="lf-remember">
            <span className={`lf-checkbox ${rememberMe ? "lf-checkbox-on" : ""}`}
              onClick={() => setRememberMe(v => !v)}
              role="checkbox"
              aria-checked={rememberMe}
              tabIndex={0}
              onKeyDown={(e) => e.key === " " && setRememberMe(v => !v)}>
              {rememberMe && <IconCheck />}
            </span>
            <span onClick={() => setRememberMe(v => !v)}>Remember me</span>
          </label>
          <button className="lf-link" onClick={() => { setStep(STEP_EMAIL); setFpError(""); setErrs({}); }}>
            Forgot password?
          </button>
        </div>

        <button className="lf-btn" onClick={handleLogin} disabled={loading}>
          {loading ? <span className="lf-spinner" /> : null}
          {loading ? "Signing In…" : "Sign In"}
        </button>

        <div className="lf-footer">Hulm IT Solution Contracting © 2026</div>
      </>
    );

    /* ── STEP 1: SEND OTP ── */
    if (step === STEP_EMAIL) return (
      <>
        <button className="lf-back" onClick={backToLogin}>
          <IconArrow /> Back to login
        </button>

        <div className="lf-fp-icon"><IconMail /></div>
        <div className="lf-title">Forgot password?</div>
        <div className="lf-sub">Enter your account email and we'll send you a 6-digit OTP.</div>

        {fpError   && <div className="lf-alert lf-alert-error">{fpError}</div>}
        {fpSuccess && <div className="lf-alert lf-alert-success">{fpSuccess}</div>}

        <label className="lf-label">Email Address</label>
        <div className={`lf-input-wrap ${errs.fpEmail ? "lf-input-err" : ""}`}>
          <IconMail />
          <input
            className="lf-input"
            type="email"
            placeholder="your@email.com"
            value={fpEmail}
            onChange={(e) => { setFpEmail(e.target.value); setErrs(p => ({...p, fpEmail: ""})); }}
            onKeyDown={(e) => e.key === "Enter" && handleSendOtp()}
          />
        </div>
        {errs.fpEmail && <p className="lf-field-err">{errs.fpEmail}</p>}

        <button className="lf-btn" onClick={handleSendOtp} disabled={fpLoading} style={{ marginTop: 20 }}>
          {fpLoading ? <span className="lf-spinner" /> : null}
          {fpLoading ? "Sending…" : "Send OTP"}
        </button>

        <div className="lf-footer">Hulm IT Solution Contracting © 2026</div>
      </>
    );

    /* ── STEP 2: VERIFY OTP ── */
    if (step === STEP_OTP) return (
      <>
        <button className="lf-back" onClick={() => { setStep(STEP_EMAIL); setOtp(["","","","","",""]); setFpError(""); setErrs({}); }}>
          <IconArrow /> Back
        </button>

        <div className="lf-fp-icon lf-fp-icon-otp"><IconKey /></div>
        <div className="lf-title">Enter OTP</div>
        <div className="lf-sub">
          We sent a 6-digit code to <strong>{fpEmail}</strong>. It expires in 10 minutes.
        </div>

        {fpError && <div className="lf-alert lf-alert-error">{fpError}</div>}

        <div className="lf-otp-row" onPaste={handleOtpPaste}>
          {otp.map((digit, i) => (
            <input
              key={i}
              id={`otp-${i}`}
              className={`lf-otp-box ${errs.otp ? "lf-otp-err" : ""} ${digit ? "lf-otp-filled" : ""}`}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleOtpChange(e.target.value, i)}
              onKeyDown={(e) => handleOtpKeyDown(e, i)}
            />
          ))}
        </div>
        {errs.otp && <p className="lf-field-err" style={{ textAlign: "center" }}>{errs.otp}</p>}

        <button className="lf-btn" onClick={handleVerifyOtp} disabled={fpLoading} style={{ marginTop: 24 }}>
          {fpLoading ? <span className="lf-spinner" /> : null}
          {fpLoading ? "Verifying…" : "Verify OTP"}
        </button>

        <div className="lf-resend">
          {resendTimer > 0 ? (
            <span className="lf-resend-timer">Resend OTP in {resendTimer}s</span>
          ) : (
            <button className="lf-link" onClick={handleSendOtp} disabled={fpLoading}>
              Resend OTP
            </button>
          )}
        </div>

        <div className="lf-footer">Hulm IT Solution Contracting © 2026</div>
      </>
    );

    /* ── STEP 3: RESET PASSWORD ── */
    if (step === STEP_RESET) return (
      <>
        <div className="lf-fp-icon lf-fp-icon-reset"><IconLock /></div>
        <div className="lf-title">Reset password</div>
        <div className="lf-sub">Choose a strong new password for your account.</div>

        {fpError && <div className="lf-alert lf-alert-error">{fpError}</div>}

        <label className="lf-label">New Password</label>
        <div className={`lf-input-wrap ${errs.newPw ? "lf-input-err" : ""}`}>
          <IconLock />
          <input
            className="lf-input"
            type={showNewPw ? "text" : "password"}
            placeholder="Min. 6 characters"
            value={newPw}
            onChange={(e) => { setNewPw(e.target.value); setErrs(p => ({...p, newPw: ""})); }}
          />
          <button className="lf-eye" onClick={() => setShowNewPw(v => !v)} type="button">
            <IconEye off={showNewPw} />
          </button>
        </div>
        {errs.newPw && <p className="lf-field-err">{errs.newPw}</p>}

        {newPw && (
          <div className="lf-pw-strength">
            <div className={`lf-pw-bar lf-pw-${newPw.length < 6 ? "weak" : newPw.length < 10 ? "fair" : "strong"}`} />
            <span className="lf-pw-label">{newPw.length < 6 ? "Too short" : newPw.length < 10 ? "Fair" : "Strong"}</span>
          </div>
        )}

        <label className="lf-label" style={{ marginTop: 14 }}>Confirm Password</label>
        <div className={`lf-input-wrap ${errs.confirmPw ? "lf-input-err" : ""}`}>
          <IconLock />
          <input
            className="lf-input"
            type={showConfPw ? "text" : "password"}
            placeholder="Re-enter password"
            value={confirmPw}
            onChange={(e) => { setConfirmPw(e.target.value); setErrs(p => ({...p, confirmPw: ""})); }}
          />
          <button className="lf-eye" onClick={() => setShowConfPw(v => !v)} type="button">
            <IconEye off={showConfPw} />
          </button>
        </div>
        {errs.confirmPw && <p className="lf-field-err">{errs.confirmPw}</p>}

        <button className="lf-btn" onClick={handleResetPassword} disabled={fpLoading} style={{ marginTop: 22 }}>
          {fpLoading ? <span className="lf-spinner" /> : null}
          {fpLoading ? "Resetting…" : "Reset Password"}
        </button>

        <div className="lf-footer">Hulm IT Solution Contracting © 2026</div>
      </>
    );

    /* ── DONE ── */
    if (step === STEP_DONE) return (
      <>
        <div className="lf-done-wrap">
          <div className="lf-done-circle"><IconCheck /></div>
          <div className="lf-title" style={{ marginTop: 20 }}>Password reset!</div>
          <div className="lf-sub">Your password has been updated successfully. You can now sign in with your new password.</div>
          <button className="lf-btn" onClick={backToLogin} style={{ marginTop: 28 }}>
            Back to Sign In
          </button>
        </div>
        <div className="lf-footer">Hulm IT Solution Contracting © 2026</div>
      </>
    );
  };

  /* ════════ PAGE ════════ */
  return (
    <div id="login" className="screen active">
      <div className="login-grid">

        {/* LEFT HERO */}
        <div className="login-hero">
          <div className="hero-content">
            <div className="hero-logo">
              <div className="lg-icon">FL</div>
              <span>Hulm IT Solution Contracting</span>
            </div>
            <h1>
              Track every job.<br />
              <em>Empower every team.</em>
            </h1>
            <p>
              A mobile-first Progressive Web App for field job management,
              activity logging, and productivity reporting.
            </p>
            <div className="hero-features">
              <div className="feat-row">✔ Real-time job status lifecycle</div>
              <div className="feat-row">✔ Activity &amp; time logging</div>
              <div className="feat-row">✔ PDF export &amp; WhatsApp share</div>
              <div className="feat-row">✔ Email notifications</div>
            </div>
          </div>
          <div className="hero-bottom">jobs.fourleafbh.com · Field Job System</div>
        </div>

        {/* RIGHT PANEL */}
        <div className="login-form-panel">
          <div className="lf-inner">
            {renderForm()}
          </div>
        </div>

      </div>
    </div>
  );
}

export default Login;